<script setup lang="ts">
  import * as changeCase from 'change-case';
  import dayjsLib from 'dayjs';
  import type dayjs from 'dayjs';
  import { computed } from 'vue';

  import { CallStatus } from '@stuut/api/models';
  import { humanize } from '@stuut/common/src/utils';
  import { Badge } from '@ui/components/badge';
  import type { BadgeVariants } from '@ui/components/badge';

  const props = withDefaults(
    defineProps<{
      status: CallStatus;
      size?: 'xs' | 'sm';
      scheduledFor?: dayjs.Dayjs | null;
    }>(),
    {
      scheduledFor: null,
      size: 'sm',
    },
  );

  const getVariant = (status: CallStatus): BadgeVariants['variant'] => {
    switch (props.status) {
      case CallStatus.PENDING_APPROVAL:
      case CallStatus.REGISTERED:
        return 'outline';
      case CallStatus.QUEUED:
      case CallStatus.INITIATED:
      case CallStatus.IN_PROGRESS:
      case CallStatus.RINGING:
        return 'open';
      case CallStatus.CANCELED:
      case CallStatus.BUSY:
        return 'warning';
      case CallStatus.AWAITING_POST_PROCESSING:
        return 'agent';
      case CallStatus.COMPLETED:
        return 'success';
      case CallStatus.NO_ANSWER:
      case CallStatus.FAILED:
        return 'error';
    }
  };

  const displayText = computed(() => {
    if (props.status === CallStatus.AWAITING_POST_PROCESSING) {
      return 'Analyzing';
    }
    if (props.status === CallStatus.QUEUED && props.scheduledFor?.isAfter(dayjsLib())) {
      return `Scheduled ${props.scheduledFor.fromNow()}`;
    }
    return changeCase.capitalCase(humanize(props.status));
  });
</script>

<template>
  <Badge :variant="getVariant(status)" :size="size" class="text-nowrap">
    {{ displayText }}
  </Badge>
</template>

<style scoped></style>
